import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CloudQueue as DeploymentIcon,
  Policy as PolicyIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import api from '../api';

interface HealthMetrics {
  healthStatus: string;
  healthScore: number;
  healthIssues: string[];
  customers: {
    TotalCustomers: number;
    ActiveCustomers: number;
    TrialCustomers: number;
    StandardCustomers: number;
    PremiumCustomers: number;
  };
  deployments: {
    TotalDeployments: number;
    CompletedDeployments: number;
    FailedDeployments: number;
    InProgressDeployments: number;
    RecentDeployments: number;
  };
  policies: {
    TotalPolicies: number;
    DeployedPolicies: number;
    FailedPolicies: number;
    CustomizedPolicies: number;
  };
  recentErrors: Array<{
    DeploymentID: number;
    CompanyName: string;
    Status: string;
    StartDate: string;
    ErrorMessage: string;
  }>;
  timestamp: string;
}

interface DeploymentStats {
  customers: Array<{
    CustomerID: number;
    CompanyName: string;
    TotalDeployments: number;
    Completed: number;
    Failed: number;
    Partial: number;
    LastDeployment: string;
  }>;
}

interface PolicyStats {
  customers: Array<{
    CustomerID: number;
    CompanyName: string;
    TotalPolicies: number;
    Deployed: number;
    Failed: number;
    Customized: number;
    Platforms: number;
  }>;
}

export default function SystemHealth() {
  const [healthData, setHealthData] = useState<HealthMetrics | null>(null);
  const [deploymentStats, setDeploymentStats] = useState<DeploymentStats | null>(null);
  const [policyStats, setPolicyStats] = useState<PolicyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const loadHealthData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load system health
      const healthResponse = await api.get('/TCAdminSupport/system-health');
      setHealthData(healthResponse.data);

      // Load deployment statistics
      try {
        const deploymentResponse = await api.get('/TCAdminSupport/deployment-stats');
        setDeploymentStats(deploymentResponse.data);
      } catch (err) {
        console.error('Failed to load deployment stats:', err);
      }

      // Load policy statistics
      try {
        const policyResponse = await api.get('/TCAdminSupport/policy-stats');
        setPolicyStats(policyResponse.data);
      } catch (err) {
        console.error('Failed to load policy stats:', err);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealthData();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadHealthData();
      }, 60000); // Refresh every 60 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case 'Healthy':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40 }} />;
      case 'Warning':
        return <WarningIcon sx={{ color: 'warning.main', fontSize: 40 }} />;
      case 'Critical':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 40 }} />;
      default:
        return null;
    }
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'Healthy':
        return 'success';
      case 'Warning':
        return 'warning';
      case 'Critical':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && !healthData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!healthData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No health data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">System Health Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={autoRefresh ? "contained" : "outlined"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            size="small"
          >
            {autoRefresh ? "Auto-Refresh On" : "Auto-Refresh Off"}
          </Button>
          <Tooltip title="Refresh now">
            <IconButton onClick={loadHealthData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overall Health Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {getHealthStatusIcon(healthData.healthStatus)}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" component="div">
                System Status: {healthData.healthStatus}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Health Score: {healthData.healthScore}/100
              </Typography>
              <LinearProgress
                variant="determinate"
                value={healthData.healthScore}
                sx={{
                  mt: 1,
                  height: 10,
                  borderRadius: 5,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: healthData.healthScore >= 90 ? 'success.main' :
                                   healthData.healthScore >= 70 ? 'warning.main' : 'error.main'
                  }
                }}
              />
            </Box>
          </Box>

          {healthData.healthIssues.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                Health Issues:
              </Typography>
              {healthData.healthIssues.map((issue, index) => (
                <Alert severity="warning" key={index} sx={{ mt: 1 }}>
                  {issue}
                </Alert>
              ))}
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Last updated: {new Date(healthData.timestamp).toLocaleString()}
          </Typography>
        </CardContent>
      </Card>

      {/* Customer Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4">{healthData.customers.ActiveCustomers}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Customers</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Trial: {healthData.customers.TrialCustomers} | 
                  Standard: {healthData.customers.StandardCustomers} | 
                  Premium: {healthData.customers.PremiumCustomers}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DeploymentIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography variant="h4">{healthData.deployments.TotalDeployments}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Deployments</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Completed: {healthData.deployments.CompletedDeployments} | 
                  Failed: {healthData.deployments.FailedDeployments} | 
                  Recent (7d): {healthData.deployments.RecentDeployments}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PolicyIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h4">{healthData.policies.TotalPolicies}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Policies</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Deployed: {healthData.policies.DeployedPolicies} | 
                  Failed: {healthData.policies.FailedPolicies} | 
                  Customized: {healthData.policies.CustomizedPolicies}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Errors */}
      {healthData.recentErrors.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Errors (Last 24 Hours)
            </Typography>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Deployment ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {healthData.recentErrors.map((err) => (
                    <TableRow key={err.DeploymentID}>
                      <TableCell>{err.DeploymentID}</TableCell>
                      <TableCell>{err.CompanyName}</TableCell>
                      <TableCell>
                        <Chip label={err.Status} color="error" size="small" />
                      </TableCell>
                      <TableCell>{new Date(err.StartDate).toLocaleString()}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'error.main' }}>
                          {err.ErrorMessage || 'No error message'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Deployment Statistics by Customer */}
      {deploymentStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Deployment Statistics by Customer
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Partial</TableCell>
                    <TableCell>Last Deployment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deploymentStats.customers.map((customer) => (
                    <TableRow key={customer.CustomerID}>
                      <TableCell>{customer.CompanyName}</TableCell>
                      <TableCell align="right">{customer.TotalDeployments}</TableCell>
                      <TableCell align="right">
                        <Chip label={customer.Completed} color="success" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={customer.Failed} color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={customer.Partial} color="warning" size="small" />
                      </TableCell>
                      <TableCell>
                        {customer.LastDeployment 
                          ? new Date(customer.LastDeployment).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Policy Statistics by Customer */}
      {policyStats && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Policy Statistics by Customer
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Deployed</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Customized</TableCell>
                    <TableCell align="right">Platforms</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policyStats.customers.map((customer) => (
                    <TableRow key={customer.CustomerID}>
                      <TableCell>{customer.CompanyName}</TableCell>
                      <TableCell align="right">{customer.TotalPolicies}</TableCell>
                      <TableCell align="right">
                        <Chip label={customer.Deployed} color="success" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={customer.Failed} color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={customer.Customized} color="warning" size="small" />
                      </TableCell>
                      <TableCell align="right">{customer.Platforms}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
